import React, { useEffect, useMemo, useState } from "react";
import api from "../services/api";
import { civicServicesService } from "../services/civicServicesService";
import { useAuth } from "../context/AuthContext";

const IconPlus = () => <span className="text-lg">+</span>;
const IconEdit = () => <span className="text-lg">✏️</span>;
const IconTrash = () => <span className="text-lg">🗑️</span>;
const IconEye = () => <span className="text-lg">👁️</span>;
const IconSave = () => <span className="text-lg">💾</span>;
const IconTimes = () => <span className="text-lg">✕</span>;

// Local placeholder images (SVG data URI)
const placeholderThumb =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect width='80' height='80' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='10' fill='%236b7280'%3ENo Icon%3C/text%3E%3C/svg%3E";

const placeholderLarge =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='220'%3E%3Crect width='320' height='220' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='%236b7280'%3EIcon Not Found%3C/text%3E%3C/svg%3E";

function normalizeBaseUrl(value) {
  if (!value) return "";
  return String(value).trim().replace(/\/+$/, "");
}

function isHttpUrl(value) {
  return /^https?:\/\//i.test(String(value || "").trim());
}

const CivicServicesManagement = () => {
  const { hasPermission } = useAuth();

  const API_BASE_URL = useMemo(() => {
    // api.defaults.baseURL already ends with /api in this project
    const fromEnv = process.env.REACT_APP_API_URL;
    const fromApi = api?.defaults?.baseURL;
    return (
      normalizeBaseUrl(fromEnv) ||
      normalizeBaseUrl(fromApi) ||
      "https://newgardenbackend.amcfest.in/api"
    );
  }, []);

  const getIconUrl = (iconValue) => {
    const icon = String(iconValue || "").trim();
    if (!icon) return null;

    // If already a full URL, use it
    if (isHttpUrl(icon)) return icon;

    // If a relative path is stored, try to resolve against server base (without /api)
    const serverBase = API_BASE_URL.replace(/\/api$/i, "");
    if (icon.startsWith("/")) return `${serverBase}${icon}`;

    // Otherwise treat as file name under backend /api/Images/icon/
    return `${API_BASE_URL}/Images/icon/${encodeURIComponent(icon)}`;
  };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [filterActive, setFilterActive] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    icon: "",
    link: "",
    sort_order: "",
    is_active: 1,
  });

  const [viewModal, setViewModal] = useState({ show: false, item: null });
  const [viewImageModal, setViewImageModal] = useState({
    show: false,
    src: "",
    title: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const active =
        filterActive === "1" ? 1 : filterActive === "0" ? 0 : undefined;
      const data = await civicServicesService.list(active);
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading civic services:", error);
      alert("Failed to load civic services");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterActive]);

  const resetForm = () => {
    setEditing(null);
    setForm({
      name: "",
      description: "",
      icon: "",
      link: "",
      sort_order: "",
      is_active: 1,
    });
    setShowForm(false);
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      description: item.description || "",
      icon: item.icon || "",
      link: item.link || "",
      sort_order:
        item.sort_order === null || item.sort_order === undefined
          ? ""
          : String(item.sort_order),
      is_active: item.is_active ? 1 : 0,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name?.trim(),
        description: form.description?.trim() || null,
        icon: form.icon?.trim() || null,
        link: form.link?.trim() || null,
        sort_order:
          form.sort_order === "" || form.sort_order === null
            ? null
            : Number(form.sort_order),
        is_active: Number(form.is_active) ? 1 : 0,
      };

      if (!payload.name) {
        alert("Name is required");
        setLoading(false);
        return;
      }

      if (editing?.id) {
        await civicServicesService.update(editing.id, payload);
        alert("Civic service updated successfully");
      } else {
        await civicServicesService.create(payload);
        alert("Civic service created successfully");
      }

      resetForm();
      await load();
    } catch (error) {
      console.error("Error saving civic service:", error);
      alert(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to save civic service"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this civic service?")) {
      return;
    }
    setLoading(true);
    try {
      await civicServicesService.remove(id);
      alert("Civic service deleted successfully");
      await load();
    } catch (error) {
      console.error("Error deleting civic service:", error);
      alert(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to delete civic service"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleIconFileUpload = async (file) => {
    if (!file) return;
    setUploadingIcon(true);
    try {
      const fd = new FormData();
      fd.append("icon", file);
      const res = await civicServicesService.uploadIcon(fd);
      const filename = res?.data?.filename;
      if (!filename) {
        throw new Error("Upload succeeded but filename missing");
      }
      setForm((p) => ({ ...p, icon: filename }));
    } catch (error) {
      console.error("Icon upload failed:", error);
      alert(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Icon upload failed"
      );
    } finally {
      setUploadingIcon(false);
    }
  };

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const hay = `${it.id} ${it.name || ""} ${it.description || ""} ${
        it.link || ""
      } ${it.icon || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, query]);

  const canWrite = hasPermission("view_permissions"); // superadmin true; fallback permission

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Civic Services</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
              disabled={!canWrite}
              title={!canWrite ? "No permission" : "Add"}
            >
              <IconPlus /> Add
            </button>
          </div>
        </div>

        {/* Search + Filter */}
        <div className="mb-4 flex gap-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, link, icon, id..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
          {(query || filterActive) && (
            <button
              onClick={() => {
                setQuery("");
                setFilterActive("");
              }}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
            >
              Clear
            </button>
          )}
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 rounded-lg shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">
                  {editing ? "Edit Civic Service" : "Add Civic Service"}
                </h3>
                <button
                  onClick={resetForm}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <IconTimes />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, name: e.target.value }))
                      }
                      className="w-full px-4 py-2 border rounded-md"
                      required
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">
                      Description
                    </label>
                    <textarea
                      value={form.description}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, description: e.target.value }))
                      }
                      className="w-full px-4 py-2 border rounded-md"
                      rows={3}
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">
                      Icon (URL / filename)
                    </label>
                    <input
                      type="text"
                      value={form.icon}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, icon: e.target.value }))
                      }
                      className="w-full px-4 py-2 border rounded-md"
                      placeholder="e.g. treecutting2.png OR https://.../icon.png"
                    />
                    <div className="mt-3">
                      <label className="block text-sm font-medium mb-2">
                        Upload Icon File
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploadingIcon || !canWrite}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleIconFileUpload(f);
                          // allow same file re-select
                          e.target.value = "";
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      />
                      {uploadingIcon && (
                        <p className="text-sm text-gray-600 mt-1">
                          Uploading icon...
                        </p>
                      )}
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                      <img
                        src={getIconUrl(form.icon) || placeholderThumb}
                        alt="Icon preview"
                        className="h-16 w-16 object-cover rounded-lg border border-gray-300 shadow-sm"
                        onError={(e) => {
                          e.target.src = placeholderThumb;
                          e.target.onerror = null;
                        }}
                      />
                      <button
                        type="button"
                        className="text-blue-600 hover:text-blue-900"
                        onClick={() => {
                          const src = getIconUrl(form.icon);
                          if (!src) return;
                          setViewImageModal({
                            show: true,
                            src,
                            title: form.name || "Icon",
                          });
                        }}
                        disabled={!form.icon}
                      >
                        Preview Large
                      </button>
                      <span className="text-xs text-gray-500">
                        Hint: backend icons path is{" "}
                        <span className="font-mono">
                          /api/Images/icon/&lt;file&gt;
                        </span>
                      </span>
                    </div>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">
                      Link (route / URL)
                    </label>
                    <input
                      type="text"
                      value={form.link}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, link: e.target.value }))
                      }
                      className="w-full px-4 py-2 border rounded-md"
                      placeholder="/tree-cutting OR https://..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Sort Order
                    </label>
                    <input
                      type="number"
                      value={form.sort_order}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, sort_order: e.target.value }))
                      }
                      className="w-full px-4 py-2 border rounded-md"
                      placeholder="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Status
                    </label>
                    <select
                      value={form.is_active}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, is_active: e.target.value }))
                      }
                      className="w-full px-4 py-2 border rounded-md"
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={loading || !canWrite}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <IconSave /> {editing ? "Update" : "Create"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 flex items-center gap-2"
                  >
                    <IconTimes /> Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading civic services...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No records found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-teal-600 to-emerald-600">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">
                      Icon
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">
                      Link
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredItems.map((item) => {
                    const iconSrc = getIconUrl(item.icon) || placeholderThumb;
                    return (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <img
                            src={iconSrc}
                            alt={item.name || "Icon"}
                            className="h-16 w-16 object-cover rounded-lg border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity shadow-sm"
                            onClick={() => {
                              setViewImageModal({
                                show: true,
                                src: getIconUrl(item.icon) || placeholderLarge,
                                title: item.name || "Icon",
                              });
                            }}
                            onError={(e) => {
                              e.target.src = placeholderThumb;
                              e.target.onclick = null;
                              e.target.onerror = null;
                            }}
                          />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="font-medium">{item.name}</div>
                          {item.description && (
                            <div
                              className="text-xs text-gray-500 max-w-sm truncate"
                              title={item.description}
                            >
                              {item.description}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 max-w-sm">
                          <div className="truncate" title={item.link || ""}>
                            {item.link || "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.sort_order ?? "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              item.is_active
                                ? "bg-blue-100 text-blue-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {item.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() =>
                                setViewModal({ show: true, item })
                              }
                              className="text-gray-700 hover:text-gray-900 p-2 rounded hover:bg-gray-100 transition-colors"
                              title="View"
                            >
                              <IconEye />
                            </button>
                            <button
                              onClick={() => handleEdit(item)}
                              className="text-blue-600 hover:text-blue-900 p-2 rounded hover:bg-blue-50 transition-colors"
                              title="Edit"
                              disabled={!canWrite}
                            >
                              <IconEdit />
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50 transition-colors"
                              title="Delete"
                              disabled={!canWrite}
                            >
                              <IconTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* View Modal */}
        {viewModal.show && viewModal.item && (
          <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
            onClick={() => setViewModal({ show: false, item: null })}
          >
            <div
              className="bg-white w-full max-w-3xl rounded-lg shadow-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-semibold">View Civic Service</h3>
                <button
                  onClick={() => setViewModal({ show: false, item: null })}
                  className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="p-6 grid grid-cols-2 gap-4">
                <div className="col-span-2 flex items-center gap-4">
                  <img
                    src={getIconUrl(viewModal.item.icon) || placeholderLarge}
                    alt={viewModal.item.name || "Icon"}
                    className="h-24 w-24 object-cover rounded-lg border border-gray-300 shadow-sm"
                    onError={(e) => {
                      e.target.src = placeholderLarge;
                      e.target.onerror = null;
                    }}
                    onClick={() => {
                      const src =
                        getIconUrl(viewModal.item.icon) || placeholderLarge;
                      setViewImageModal({
                        show: true,
                        src,
                        title: viewModal.item.name || "Icon",
                      });
                    }}
                  />
                  <div>
                    <div className="text-xl font-bold">{viewModal.item.name}</div>
                    <div className="text-sm text-gray-600">
                      ID: {viewModal.item.id}
                    </div>
                  </div>
                </div>

                <div className="col-span-2">
                  <div className="text-sm font-semibold text-gray-700">
                    Description
                  </div>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {viewModal.item.description || "-"}
                  </div>
                </div>

                <div className="col-span-2">
                  <div className="text-sm font-semibold text-gray-700">Link</div>
                  <div className="text-sm text-gray-700 break-all">
                    {viewModal.item.link || "-"}
                  </div>
                </div>

                <div>
                  <div className="text-sm font-semibold text-gray-700">
                    Sort Order
                  </div>
                  <div className="text-sm text-gray-700">
                    {viewModal.item.sort_order ?? "-"}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-700">Status</div>
                  <div className="text-sm text-gray-700">
                    {viewModal.item.is_active ? "Active" : "Inactive"}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t flex justify-end gap-2">
                <button
                  onClick={() => {
                    setViewModal({ show: false, item: null });
                    handleEdit(viewModal.item);
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
                  disabled={!canWrite}
                >
                  <IconEdit /> Edit
                </button>
                <button
                  onClick={() => setViewModal({ show: false, item: null })}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 flex items-center gap-2"
                >
                  <IconTimes /> Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Image View Modal */}
        {viewImageModal.show && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() => setViewImageModal({ show: false, src: "", title: "" })}
          >
            <div
              className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-auto relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">
                  {viewImageModal.title}
                </h3>
                <button
                  onClick={() =>
                    setViewImageModal({ show: false, src: "", title: "" })
                  }
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="p-6">
                <img
                  src={viewImageModal.src}
                  alt={viewImageModal.title}
                  className="max-w-full h-auto rounded-lg shadow-lg"
                  onError={(e) => {
                    e.target.src = placeholderLarge;
                    e.target.onerror = null;
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CivicServicesManagement;

